// 11 §10.2 Lobby: the parties of the World (state 0 forming), create one, join with my heroes, the
// line-up with sprites and owners, party chat, and the lead's "Set out" → start_encounter.
// 2026-10-23 mockup pass ("Party lobby — Wardens of the Vale"): one framed window — a top bar (back,
// eyebrow over the name, the World's who-may-join), a row of seat cards (player colour strip, the
// hero at 3× on a plinth, class · level, HP / Move / resource from `preview_hero`, a status chip;
// open seats dashed), YOUR HERO tiles + a context panel, the chat column, and a bottom bar (ready
// squares + summary, Leave, the one gold action). The frame pieces below are shared with the World
// lobby (Gathering.tsx); their classes `fk-lobby__*` live in lobby.css.
import { createContext, useContext, useEffect, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { EmoteAnchor, PetWiggle } from '../emote/EmoteBubble';
import { openHeroSheet } from '../heroes/heroSheetStore';
import '../heroes/heroes.css';
import { OFFLINE_TITLE, useLinkUp } from '../../core/stores/link';
import { Banner, Button, ClassIcon, Field, HeroSprite, Input, Modal, Segmented, Select, SkeletonList, confirmDialog, useSettled } from '../components';
import { useSession } from '../../core/stores/session';
import { useRoute } from '../../core/stores/route';
import { useHeroCallings, useHeroOrigins, useMyHeroes, useNames, useParties, usePartyInstances, usePartyMembers, useProfileMap, useWorld, useWorldClock, useWorldHeroes, useWorldMembers, useWorldPartyMembers, useWorldSettings } from '../../core/subscriptions/hooks';
import { rpc, useToasts } from '../../core/net/reducers';
import { identityHex } from '../../core/net/connection';
import { CLASS_NAMES, SPECIES_NAMES } from '../../core/sim/adapt';
import { INST } from '../../core/adventure/graph';
import { getBridge } from '../../core/encounter/session';
import type { Hero, HeroCalling, HeroOrigin, WorldOptions } from '../../bindings/types';
import { COMPANION_KINDS, coatFrame, companionIdOf, isCompanionPick } from '../../core/sim/companion';
import { ChatDock, playerColour, usePlayerColours } from './ChatDock';
import { FriendsPanel } from './PlayOnline';
import { HeroForge } from './HeroForge';
import { useFounding } from './Outfitting';
import { preloadEncounterSfx } from '../../core/audio';
import { townBand, usePlace } from '../../core/audio/places';
import { worldTitle } from '../../core/story/worldName';

const PARTY_STATE = ['forming', 'in the field', 'disbanded'];
/** `create_party(world, name, scenario)` quick-fight fixtures (fk-world `start_encounter`). */
// docs/60: 4 is the practice crawl (3, the bog beyond the weir, is the opening's own and not offered);
// docs/61: 5 the practice exploration
export const SCENARIOS: Readonly<Record<number, string>> = { 0: 'The Mirewood dam', 1: 'The bog road', 2: 'A puzzle room (a new one each time)', 4: 'A dungeon crawl (practice)', 5: 'An exploration (practice)' };
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
export const windowLabel = (s: number) => (s === 0 ? 'unlimited' : s < 60 ? `${s} s` : `${s / 60} min`);

// ───────────────────────── shared frame pieces (party lobby + World lobby) ─────────────────────────

export interface HeroStats { hp: number; move: number; resource?: { kind: string; max: number } }
const statsCache = new Map<string, HeroStats | null>();
/** HP, Move and the class resource: `derive_stats` over the hero's origin kit at its level (docs/17 §4). */
export function heroStats(h: Hero, o: HeroOrigin | undefined, k?: { pick: number; spells: number[] }): HeroStats | undefined {
  const bridge = getBridge();
  if (!bridge || !o) return undefined;
  const key = `${h.class}.${h.species}.${h.build}.${o.background}.${o.loadout}.${h.level}.${h.cards.join(',')}.${k?.pick ?? 0}`;
  if (!statsCache.has(key)) {
    try {
      // docs/59: with the hero's calling (a patron's guard, Ommar's HP, a gnome's extra resource)
      const s = bridge.previewHero(h.class, h.species, h.build, o.background, o.loadout, h.level, k?.pick ?? 0, k?.spells ?? []).stats;
      // the preview has no cards: Hardy (1) and Fleet (5) as `derive_stats` adds them (docs/58 FO3)
      const hardy = h.cards.filter((c) => c === 1).length, fleet = h.cards.filter((c) => c === 5).length;
      statsCache.set(key, { hp: Math.min(90, s.hp_max + 3 * hardy), move: Math.min(6, s.move_tiles + fleet), resource: s.resource.max > 0 ? { kind: s.resource.kind, max: s.resource.max } : undefined });
    } catch { statsCache.set(key, null); }
  }
  return statsCache.get(key) ?? undefined;
}

const seatStyle = (colour: number | undefined) => ({ '--fk-seat': playerColour(colour) }) as CSSProperties;

/** The screen's window: top bar, body (main column + chat column), bottom bar. */
export function LobbyFrame({ screen, testId, top, main, side, bottom, ...rest }: { screen: string; testId: string; top: ReactNode; main: ReactNode; side: ReactNode; bottom: ReactNode } & HTMLAttributes<HTMLElement>) {
  return (
    <main className={`fk-screen fk-screen--lobbyframe fk-screen--${screen}`} data-testid={testId} {...rest}>
      <div className="fk-lobby__frame fk-hit">
        {top}
        <div className="fk-lobby__body">
          <div className="fk-lobby__main">{main}</div>
          <aside className="fk-lobby__side">{side}</aside>
        </div>
        {bottom}
      </div>
    </main>
  );
}

export function LobbyTop({ back, onBack, backTestId, eyebrow, name, nameTestId, right }: { back: string; onBack: () => void; backTestId?: string; eyebrow: string; name: ReactNode; nameTestId?: string; right?: ReactNode }) {
  return (
    <header className="fk-lobby__top">
      <button type="button" className="fk-lobby__back" onClick={onBack} data-testid={backTestId}><span aria-hidden="true">‹</span> {back}</button>
      <div className="fk-lobby__title">
        <span className="fk-lobby__eyebrow">{eyebrow}</span>
        <h1 className="fk-lobby__name" data-testid={nameTestId}>{name}</h1>
      </div>
      {right && <div className="fk-lobby__top-right">{right}</div>}
    </header>
  );
}

/** Who may join (`WorldOptions.visibility`), in the mockup's order; only the keeper may change it. */
export function JoinPolicy({ value, keeper, onChange }: { value: number; keeper: boolean; onChange: (v: number) => void }) {
  return (
    <div className="fk-lobby__policy" data-testid="opt-visibility" data-value={value} title={keeper ? 'Who may join without an invitation' : 'Set by the keeper'}>
      <Segmented label="Who may join" value={value} disabled={!keeper} onChange={onChange} options={[{ value: 1, label: 'Friends', title: 'Friends of the keeper' }, { value: 0, label: 'Invite only', title: 'Invitation only' }, { value: 2, label: 'Open', title: 'Open to all' }]} />
    </div>
  );
}

/** The keeper's join-policy change from either lobby (the rest of the options ride along unchanged). */
export const setVisibility = (worldId: bigint, options: WorldOptions, v: number) => { if (v !== options.visibility) void rpc.updateWorldSettings(worldId, { ...options, visibility: v }); };

export type SeatTone = 'ready' | 'choosing' | 'idle';

/** How tightly the seat cards pack (docs/50 PC3): one row up to five cards, two rows up to ten, then
 *  a muster grid of up to seven columns, so twenty players (and their eighty heroes) fit the window. */
export type SeatDensity = 'roomy' | 'rows' | 'muster';
export function seatLayout(cards: number): { density: SeatDensity; cols: number } {
  if (cards <= 5) return { density: 'roomy', cols: Math.max(cards, 1) };
  if (cards <= 10) return { density: 'rows', cols: Math.ceil(cards / 2) };
  // as few rows as seven columns allow (two at the least): 13 seats are two rows of seven, 20 three
  const rows = Math.max(2, Math.ceil(cards / 7));
  return { density: 'muster', cols: Math.ceil(cards / rows) };
}
const DensityContext = createContext<SeatDensity>('roomy');

/** The seat cards' container: `cards` counts every card inside, open seats included. */
export function SeatRow({ cards, children, className = '', style, ...rest }: { cards: number; children: ReactNode; className?: string } & HTMLAttributes<HTMLDivElement>) {
  const { density, cols } = seatLayout(cards);
  return (
    <div className={`fk-lobby__seats fk-lobby__seats--${density} ${className}`} data-density={density} style={{ ...style, '--fk-seat-cols': cols } as CSSProperties} {...rest}>
      <DensityContext.Provider value={density}>{children}</DensityContext.Provider>
    </div>
  );
}

/** A ranger's beast as the lobby shows it (docs/59 companions): its name, its kind and its sprite. */
export interface SeatBeast { name: string; kind: string; frame: number }
/** The companion of `heroId` among the World's `hero_calling` rows, if the hero is a ranger with one. */
export function beastOf(callings: readonly HeroCalling[] | undefined, heroId: bigint): SeatBeast | undefined {
  const c = callings?.find((k) => k.heroId === heroId);
  if (!c || !isCompanionPick(c.pick)) return undefined;
  const kind = COMPANION_KINDS[c.pick] ?? 'Companion';
  const body = getBridge()?.callings()?.companions.find((b) => b.calling === c.pick);
  const frame = body ? coatFrame(body, c.petCoat) : 0;
  return { name: c.petName.trim() || kind, kind, frame };
}
/** "Reggie the Wolf"; an unnamed beast is just "Wolf". */
const beastLine = (b: SeatBeast) => (b.name === b.kind ? b.kind : `${b.name} the ${b.kind}`);

/** One seat: player strip, the hero(es) on a plinth (a ranger's beast at their heel), name, the
 *  beast's name, class · level, stats, status chip. */
export function SeatCard({ colour, player, crown, crownTitle = 'keeper', you, heroes, callings, stats, tone, status, tools, heroTestId, className = '', ...rest }: {
  colour?: number; player: string; crown?: boolean; crownTitle?: string; you?: boolean; heroes: Hero[]; callings?: readonly HeroCalling[]; stats?: HeroStats; tone: SeatTone; status: string; tools?: ReactNode; heroTestId?: string; className?: string;
} & HTMLAttributes<HTMLElement>) {
  const density = useContext(DensityContext);
  const one = heroes.length === 1 ? heroes[0] : undefined;
  const beasts = new Map(heroes.map((h) => [h.id, beastOf(callings, h.id)] as const));
  const beastLines = heroes.flatMap((h) => { const b = beasts.get(h.id); return b ? [beastLine(b)] : []; }).join(' · ');
  const named = (h: Hero) => { const b = beasts.get(h.id); return b ? `${h.name} & ${beastLine(b)}` : h.name; };
  const classOf = (h: Hero) => CLASS_NAMES[h.class] ?? 'fighter';
  // docs/155 LC2: the hero's people beside their calling ("Elf Ranger")
  const kindOf = (h: Hero) => `${cap(SPECIES_NAMES[h.species] ?? '')} ${cap(classOf(h))}`.trim();
  // a lone hero stands at 3× in the single row; in rows and the muster everyone stands at 2×
  const scale = one && density === 'roomy' ? 3 : 2;
  const icon = (h: Hero) => <ClassIcon key={h.id.toString()} classId={classOf(h)} title={`${h.name} · ${kindOf(h)} · Lv ${h.level}`} />;
  return (
    <article className={`fk-lobby__seat fk-lobby__seat--${tone} ${you ? 'fk-lobby__seat--me' : ''} ${className}`} style={seatStyle(colour)} {...rest}>
      <header className="fk-lobby__seat-head">
        {crown && <span className="fk-lobby__crown" title={crownTitle} aria-label={crownTitle}>♛</span>}
        <span className="fk-lobby__seat-player">{player}</span>{you && <span className="fk-lobby__you">(you)</span>}
        {tools}
      </header>
      <div className="fk-lobby__plinth">
        <div className="fk-lobby__sprites">
          {heroes.length === 0 && <span className="fk-lobby__nohero" aria-hidden="true">?</span>}
          {heroes.map((h, _i, _a, b = beasts.get(h.id)) => [<button type="button" key={h.id.toString()} className="fk-lobby__sprite fk-hero-open" data-testid={heroTestId} data-hero-name={h.name} data-hero-id={h.id.toString()} onClick={() => openHeroSheet(h.id)} aria-label={`${h.name}: see their sheet`}><HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={classOf(h)} scale={scale} title={h.name} /><EmoteAnchor heroes={[{ id: h.id, name: h.name, mine: !!you }]} /></button>,
            b && <span key={`${h.id}-beast`} className="fk-lobby__beast" data-testid="seat-beast" data-beast-name={b.name} data-beast-kind={b.kind}><PetWiggle pet={{ id: BigInt(companionIdOf(Number(h.id))), name: b.name, mine: !!you }}><HeroSprite frame={b.frame} classId="companion" scale={2} title={beastLine(b)} /></PetWiggle></span>])}
        </div>
      </div>
      {density === 'muster' && heroes.length > 0
        // the muster card has one line under the plinth: the lone hero's class and name, or the squad's classes
        ? <div className="fk-lobby__seat-class fk-lobby__seat-class--icons" title={heroes.map((h) => `${named(h)} (${kindOf(h)} ${h.level})`).join(' · ')}>{one ? <>{icon(one)}<span className="fk-lobby__seat-lone">{one.name}</span></> : heroes.map(icon)}</div>
        : <>
          <div className="fk-lobby__seat-hero" title={heroes.map(named).join(' · ')}>{heroes.length === 0 ? <span className="fk-lobby__seat-none">no hero yet</span> : heroes.map((h) => h.name).join(' · ')}</div>
          {beastLines && <div className="fk-lobby__seat-beast" title={beastLines} data-testid="seat-beast-name">{beastLines}</div>}
          {one
            ? <div className="fk-lobby__seat-class"><ClassIcon classId={classOf(one)} title={kindOf(one)} /><span>{kindOf(one)} · Lv {one.level}</span></div>
            : heroes.length > 1 && (density === 'roomy'
              ? <div className="fk-lobby__seat-class"><span>{heroes.map((h) => `${kindOf(h)} ${h.level}`).join(' · ')}</span></div>
              : <div className="fk-lobby__seat-class fk-lobby__seat-class--icons">{heroes.map(icon)}</div>)}
        </>}
      {one && stats && density === 'roomy' && (
        <div className="fk-lobby__seat-stats">
          <span>HP <b>{stats.hp}</b></span><span>Move <b>{stats.move}</b></span>{stats.resource && <span>{cap(stats.resource.kind)} <b>{stats.resource.max}</b></span>}
        </div>
      )}
      <span className={`fk-lobby__chip fk-lobby__chip--${tone}`}>{status}</span>
    </article>
  );
}

export function OpenSeat({ title = 'Open seat', text, action, ...rest }: { title?: string; text: ReactNode; action?: ReactNode } & HTMLAttributes<HTMLElement>) {
  return (
    <article className="fk-lobby__seat fk-lobby__seat--open" {...rest}>
      <span className="fk-lobby__plus" aria-hidden="true">+</span>
      <h3 className="fk-lobby__open-title">{title}</h3>
      <p className="fk-lobby__open-text">{text}</p>
      {action}
    </article>
  );
}

/** One of my heroes in the YOUR HERO strip (a button when it does something). */
export function HeroTile({ hero, on, dim, onClick, extra, title, ...rest }: { hero: Hero; on?: boolean; dim?: boolean; onClick?: () => void; extra?: ReactNode; title?: string } & Omit<HTMLAttributes<HTMLElement>, 'onClick'>) {
  const cls = CLASS_NAMES[hero.class] ?? 'fighter';
  const body = (
    <>
      <HeroSprite heroId={hero.id} species={hero.species} frame={hero.oryxFrame} classId={cls} scale={2} title={hero.name} />
      <span className="fk-lobby__tile-name">{hero.name}</span>
      <span className="fk-lobby__tile-sub">{cap(SPECIES_NAMES[hero.species] ?? '')} {cap(cls)} · Lv {hero.level}</span>
    </>
  );
  const className = `fk-lobby__tile ${on ? 'fk-lobby__tile--on' : ''} ${dim ? 'fk-lobby__tile--dim' : ''}`;
  return onClick
    ? <button type="button" className={className} onClick={onClick} aria-pressed={!!on} title={title} data-hero-name={hero.name} {...rest}>{body}</button>
    : <div className={className} title={title} data-hero-name={hero.name} {...rest}>{body}{extra}</div>;
}

export function NewHeroTile({ disabled, onClick, title, ...rest }: { disabled?: boolean; onClick: () => void; title?: string } & Omit<HTMLAttributes<HTMLButtonElement>, 'onClick'>) {
  return <button type="button" className="fk-lobby__tile fk-lobby__tile--new" disabled={disabled} onClick={onClick} title={title} {...rest}><span>+ New hero</span></button>;
}

/** Ready squares + the one-line summary on the left of the bottom bar; actions on the right. */
export function LobbyBottom({ squares, summary, summaryTestId, actions }: { squares: Array<{ key: string; colour?: number; on: boolean; title: string }>; summary: ReactNode; summaryTestId?: string; actions: ReactNode }) {
  return (
    <footer className="fk-lobby__bottom">
      <div className="fk-lobby__summary">
        <span className="fk-lobby__squares" aria-hidden="true">{squares.map((q) => <span key={q.key} className={`fk-lobby__square ${q.on ? 'fk-lobby__square--on' : ''}`} style={seatStyle(q.colour)} title={q.title} />)}</span>
        <span className="fk-lobby__summary-text" data-testid={summaryTestId}>{summary}</span>
      </div>
      <div className="fk-lobby__actions">{actions}</div>
    </footer>
  );
}

/** A name in its player colour (summaries, waiting-on lines). */
export const Who = ({ name, colour }: { name: string; colour?: number }) => <b className="fk-lobby__who" style={{ color: playerColour(colour) }}>{name}</b>;

/** "Jo, Priya and Ash" with each name in colour. */
export function nameList(list: Array<{ name: string; colour?: number; key: string }>): ReactNode {
  return list.map((x, i) => <span key={x.key}>{i > 0 && (i === list.length - 1 ? ' and ' : ', ')}<Who name={x.name} colour={x.colour} /></span>);
}

/** Members marked online in their profile ("N here"); undefined when no member has a profile. */
export function useHere(worldId: bigint | undefined): number | undefined {
  const members = useWorldMembers(worldId);
  const profiles = useProfileMap();
  const known = members.map((m) => profiles.get(identityHex(m.owner))).filter((p) => p !== undefined);
  return known.length ? known.filter((p) => p.online).length : undefined;
}

// ───────────────────────── the party lobby ─────────────────────────

export function LobbyScreen() {
  const up = useLinkUp();
  // docs/34: fetch the fight's sounds while the party gathers, so the foe's first growl is ready
  useEffect(() => { preloadEncounterSfx(); }, []);
  const worldId = useSession((s) => s.worldId);
  // docs/44: the band musters in the haven's streets, by day or (one week in three) by night
  usePlace({ key: 'place.town', band: townBand(useWorldClock(worldId)?.weekIndex) });
  const me = useSession((s) => s.identity ?? '');
  const phase = useSession((s) => s.phase);
  const partyId = useRoute((s) => s.partyId);
  const go = useRoute((s) => s.go);
  const world = useWorld(worldId);
  const settings = useWorldSettings(worldId);
  const parties = useParties(worldId).filter((p) => p.state !== 2).sort((a, b) => (a.id < b.id ? -1 : 1));
  const party = parties.find((p) => p.id === partyId);
  const members = usePartyMembers(party ? partyId : undefined);
  const allMembers = useWorldPartyMembers(worldId);
  const heroes = useWorldHeroes(worldId);
  const origins = useHeroOrigins(worldId);
  const callings = useHeroCallings(worldId);
  const callingOf = (heroId: bigint) => callings.find((k) => k.heroId === heroId);
  const myHeroes = useMyHeroes().filter((h) => h.worldId === worldId && h.status !== 2).sort((a, b) => (a.id < b.id ? -1 : 1));
  const names = useNames(worldId);
  const colours = usePlayerColours(worldId);
  const here = useHere(worldId);
  const settled = useSettled(1000, worldId);
  const push = useToasts((s) => s.push);
  const [newParty, setNewParty] = useState('');
  const [scenario, setScenario] = useState(0);
  const instances = usePartyInstances(party ? partyId : undefined);
  const adventure = instances.find((i) => i.state === INST.LOADED || i.state === INST.IN_ENCOUNTER);
  const [busy, setBusy] = useState<string | undefined>();
  const [forging, setForging] = useState(false);
  const [inviting, setInviting] = useState(false);

  const heroById = new Map(heroes.map((h) => [h.id.toString(), h]));
  // docs/36: a hero waiting in another forming party (a band on the World screen) may be pulled in here
  const formingElsewhere = new Set(parties.filter((p) => p.state === 0 && p.id !== partyId).map((p) => p.id.toString()));
  const busyHeroes = new Set(allMembers.filter((m) => !formingElsewhere.has(m.partyId.toString())).map((m) => m.heroId.toString()));
  const isLead = party ? identityHex(party.lead) === me : false;
  const deployed = members.filter((m) => m.deployed).length;
  const blocked = phase === 'incompatible';
  const keeper = world ? identityHex(world.owner) === me : false;
  // docs/58 FO2: more heroes are recruited only while outfitting (a free one if all have retired)
  const { recruit } = useFounding(worldId);
  const rosterFull = !recruit.ok;
  const sortedMembers = [...members].sort((a, b) => (a.lead !== b.lead ? (a.lead ? -1 : 1) : a.id < b.id ? -1 : 1));
  const mineInParty = members.filter((m) => identityHex(m.owner) === me);
  const nameOf = (hex: string) => names[hex] ?? hex.slice(-6);
  const originOf = (heroId: bigint) => origins.find((o) => o.heroId === heroId);

  const createParty = async () => {
    if (!worldId || !newParty.trim()) return;
    const before = new Set(parties.map((p) => p.id.toString()));
    setBusy('create-party');
    const r = await rpc.createParty(worldId, newParty.trim(), scenario);
    setBusy(undefined);
    if (r.ok) { setNewParty(''); (window as unknown as { __fkPickNewParty?: Set<string> }).__fkPickNewParty = before; }
  };
  const pending = (window as unknown as { __fkPickNewParty?: Set<string> }).__fkPickNewParty;
  useEffect(() => {
    if (!pending) return;
    const p = parties.find((x) => !pending.has(x.id.toString()) && identityHex(x.lead) === me);
    if (p) { (window as unknown as { __fkPickNewParty?: Set<string> }).__fkPickNewParty = undefined; go('lobby', { partyId: p.id }); }
  }, [parties, pending, me, go]);

  const remove = async (heroId: bigint, name: string) => {
    if (!party) return;
    const ok = await confirmDialog({ title: `Stand ${name} down?`, text: 'They go back to the roster and can join another party.', confirmLabel: 'Stand down' });
    if (!ok) return;
    setBusy(`remove-${heroId}`);
    await rpc.leaveParty(party.id, heroId);
    setBusy(undefined);
  };
  const leave = async () => {
    if (!party) return;
    const ok = await confirmDialog({ title: `Leave ${party.name}?`, text: 'Your heroes stand down and go back to the roster.', confirmLabel: 'Leave party' });
    if (!ok) return;
    setBusy('leave');
    for (const m of mineInParty) await rpc.leaveParty(party.id, m.heroId);
    setBusy(undefined);
    go('lobby', { partyId: undefined });
  };
  const setOut = async () => {
    if (!party) return;
    setBusy('set-out');
    const r = await rpc.startEncounter(party.id);
    setBusy(undefined);
    if (r.ok) push(`${party.name} sets out for ${SCENARIOS[party.scenario] ?? 'the field'}.`, 'success');
  };
  const addHero = async (heroId: bigint, name: string) => {
    if (!party) return;
    setBusy(`add-${heroId}`);
    const r = await rpc.joinParty(party.id, heroId);
    setBusy(undefined);
    if (r.ok) push(`${name} joins ${party.name}.`);
  };

  // ── top bar ──
  const top = (
    <LobbyTop back={party ? 'Parties' : 'World'} onBack={() => (party ? go('lobby', { partyId: undefined }) : go('world'))} backTestId="lobby-back"
      eyebrow={party ? `Party lobby · ${PARTY_STATE[party.state]}` : 'Party lobby'} name={party ? party.name : 'Form a party'} nameTestId="lobby-name"
      right={settings && worldId !== undefined ? <JoinPolicy value={settings.options.visibility} keeper={keeper} onChange={(v) => setVisibility(worldId, settings.options, v)} /> : undefined} />
  );

  // ── seat row: the party's line-up, or the parties of the World and a new one ──
  const seats = party ? (
    <SeatRow cards={sortedMembers.length + (party.state === 0 ? 1 : 0)} data-testid="party-members">
      {sortedMembers.map((m) => {
        const h = heroById.get(m.heroId.toString());
        const owner = identityHex(m.owner);
        const canRemove = party.state === 0 && (owner === me || isLead);
        return (
          <SeatCard key={m.id.toString()} colour={colours.get(owner)} player={nameOf(owner)} crown={m.lead} crownTitle="party lead" you={owner === me}
            heroes={h ? [h] : []} callings={callings} stats={h ? heroStats(h, originOf(h.id), callingOf(h.id)) : undefined} tone={m.deployed ? 'ready' : 'idle'} status={m.deployed ? 'Deployed' : 'Reserve'}
            tools={canRemove ? <button type="button" className="fk-lobby__x" disabled={!!busy} onClick={() => void remove(m.heroId, h?.name ?? 'the hero')} title={`Stand ${h?.name ?? 'the hero'} down`} aria-label={`Stand ${h?.name ?? 'the hero'} down`} data-testid="member-remove">✕</button> : undefined}
            data-testid="party-member" data-hero-name={h?.name} />
        );
      })}
      {party.state === 0 && (
        <OpenSeat data-testid="seat-open" text={members.length === 0 ? 'Add your heroes below; the lead sets out once one is deployed.' : 'Any hero of this World may fall in, up to twenty deployed.'}
          action={<Button small onClick={() => setInviting(true)} data-testid="seat-invite">Invite a friend</Button>} />
      )}
    </SeatRow>
  ) : (
    <div className="fk-lobby__seats" data-testid="party-list">
      {blocked && <Banner tone="warn" inline>Client incompatible with the module: the lobby is read-only (11 §11.4).</Banner>}
      {parties.length === 0 && !settled && <div className="fk-lobby__seat fk-lobby__seat--open"><SkeletonList n={2} label="Loading parties" /></div>}
      {parties.map((p) => {
        const lead = identityHex(p.lead);
        const pm = allMembers.filter((m) => m.partyId === p.id);
        const line = pm.map((m) => heroById.get(m.heroId.toString())).filter((h): h is Hero => !!h).slice(0, 4);
        return (
          <article key={p.id.toString()} className="fk-lobby__seat fk-lobby__seat--party" style={seatStyle(colours.get(lead))} data-testid="party-item" data-party-name={p.name}>
            <header className="fk-lobby__seat-head"><span className="fk-lobby__crown" title="party lead">♛</span><span className="fk-lobby__seat-player">{nameOf(lead)}</span>{lead === me && <span className="fk-lobby__you">(you)</span>}</header>
            <div className="fk-lobby__plinth"><div className="fk-lobby__sprites">
              {line.length === 0 ? <span className="fk-lobby__nohero" aria-hidden="true">⚑</span> : line.map((h) => <HeroSprite key={h.id.toString()} heroId={h.id} species={h.species} frame={h.oryxFrame} classId={CLASS_NAMES[h.class] ?? 'fighter'} scale={2} title={h.name} />)}
            </div></div>
            <div className="fk-lobby__seat-hero">{p.name}</div>
            <div className="fk-lobby__seat-class"><span>{pm.length} hero{pm.length === 1 ? '' : 'es'} · {SCENARIOS[p.scenario] ?? `scenario ${p.scenario}`}</span></div>
            <Button small onClick={() => go('lobby', { partyId: p.id })} data-testid="party-open">Open</Button>
            <span className={`fk-lobby__chip fk-lobby__chip--${p.state === 0 ? 'ready' : 'choosing'}`}>{PARTY_STATE[p.state]}</span>
          </article>
        );
      })}
      <article className="fk-lobby__seat fk-lobby__seat--open fk-lobby__seat--create" aria-label="New party">
        <span className="fk-lobby__plus" aria-hidden="true">+</span>
        <h3 className="fk-lobby__open-title">New party</h3>
        <Field label="Party name" htmlFor="fk-party-name">
          <Input id="fk-party-name" data-testid="party-create-name" placeholder="e.g. Wardens of the Vale" value={newParty} onChange={(e) => setNewParty(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void createParty(); }} disabled={blocked} />
        </Field>
        <Field label="Quick fight" htmlFor="fk-party-scenario">
          <Select id="fk-party-scenario" data-testid="party-scenario" value={scenario} onChange={(e) => setScenario(Number(e.target.value))} disabled={blocked} title="The fixture a quick fight (Set out) opens">
            {Object.entries(SCENARIOS).map(([i, s]) => <option key={s} value={i}>{s}</option>)}
          </Select>
        </Field>
        <Button primary block disabled={blocked || !newParty.trim()} loading={busy === 'create-party'} onClick={() => void createParty()} data-testid="party-create">Create party</Button>
      </article>
    </div>
  );

  // ── YOUR HERO strip ──
  const strip = (
    <section className="fk-lobby__panel fk-lobby__mine" aria-label="Your heroes" data-testid="my-party-heroes">
      <div className="fk-lobby__panel-head"><h2 className="fk-lobby__panel-title">Your heroes</h2><span className="fk-lobby__panel-note">{party?.state === 0 ? 'Pick one to fall in · pick again to stand down' : party ? 'The party is in the field' : 'Open a party to bring them'}</span></div>
      <div className="fk-lobby__tiles">
        {myHeroes.map((h) => {
          const inThis = mineInParty.some((m) => m.heroId === h.id);
          const free = h.status === 0 && !busyHeroes.has(h.id.toString());
          if (party && party.state === 0 && inThis) return <HeroTile key={h.id.toString()} hero={h} on onClick={() => void remove(h.id, h.name)} title={`Stand ${h.name} down`} data-testid="my-party-hero" />;
          if (party && party.state === 0 && free) return <HeroTile key={h.id.toString()} hero={h} onClick={blocked || busy ? undefined : () => void addHero(h.id, h.name)} title={`${h.name} falls in`} data-testid="add-hero" />;
          return <HeroTile key={h.id.toString()} hero={h} on={inThis} dim={!free && !inThis} title={inThis ? 'in this party' : free ? h.name : h.status === 1 ? 'deployed' : 'in another party'} />;
        })}
        {worldId !== undefined && <NewHeroTile disabled={rosterFull || blocked} onClick={() => setForging(true)} title={recruit.why ?? (recruit.fee ? `Recruit a hero for ${recruit.fee} founding gold` : 'Forge a new hero')} data-testid="lobby-forge-open" />}
      </div>
    </section>
  );

  // ── context: the road ahead (party) or the World (no party) ──
  const context = party ? (
    <section className="fk-lobby__panel fk-lobby__context" aria-label="The road" data-testid="party-road">
      <span className="fk-lobby__eyebrow">{adventure ? 'Adventure · under way' : party.state === 1 ? 'In the field' : 'Quick fight'}</span>
      <h2 className="fk-lobby__context-title">{adventure ? adventure.title : SCENARIOS[party.scenario] ?? `Scenario ${party.scenario}`}</h2>
      <p className="fk-lobby__context-text">
        {adventure ? 'The party’s adventure is loaded; the local map picks up where you left it.'
          : party.state === 1 ? 'The party is on the board now.'
          : <>Set out opens this fight. For a full adventure the lead accepts an offer on the World’s quest board.</>}
      </p>
      <p className="fk-lobby__facts">Lead <Who name={nameOf(identityHex(party.lead))} colour={colours.get(identityHex(party.lead))} /> · planning window {windowLabel(party.planningWindowS)} · pausing {party.allowPausing ? 'on' : 'off'} · {deployed} deployed</p>
      <div className="fk-lobby__context-actions">
        {adventure && <Button onClick={() => go('localmap', { instanceId: adventure.id })} data-testid="to-localmap">Local map: {adventure.title}</Button>}
        {party.state === 0 && isLead && !adventure && <Button small onClick={() => go('world')} data-testid="to-offers" title="Accept an offer on the World screen's board for a full adventure">Quest board →</Button>}
      </div>
    </section>
  ) : (
    <section className="fk-lobby__panel fk-lobby__context" aria-label="The World">
      <span className="fk-lobby__eyebrow">The World</span>
      <h2 className="fk-lobby__context-title">{world ? worldTitle(world) : '…'}</h2>
      <p className="fk-lobby__context-text">A party is how heroes from different players take the same road. {parties.length ? `Open one of the ${parties.length} forming, or start your own.` : 'Name one to start; the others can fall in with their heroes.'}</p>
      {settings && <p className="fk-lobby__facts">Planning window {windowLabel(settings.options.planningWindowS)} · peril {settings.options.perilPct}% · XP {settings.options.xpRatePct}%</p>}
    </section>
  );

  // ── bottom bar ──
  const leadHex = party ? identityHex(party.lead) : '';
  const bottom = party ? (
    <LobbyBottom squares={sortedMembers.map((m) => ({ key: m.id.toString(), colour: colours.get(identityHex(m.owner)), on: m.deployed, title: heroById.get(m.heroId.toString())?.name ?? '' }))}
      summary={<><b>{deployed} deployed.</b> {party.state !== 0 ? 'The party is in the field.' : isLead ? (deployed ? 'You lead: set out when everyone is in.' : 'You lead: add a hero to set out.') : <>Waiting on <Who name={nameOf(leadHex)} colour={colours.get(leadHex)} /> to set out.</>}</>}
      actions={<>
        {party.state === 0 && mineInParty.length > 0 && <Button size="lg" disabled={!!busy} loading={busy === 'leave'} onClick={() => void leave()} data-testid="party-leave">Leave party</Button>}
        {party.state === 0 && (
          <Button primary size="lg" disabled={!up || blocked || !isLead || deployed === 0} loading={busy === 'set-out'} onClick={() => void setOut()} data-testid="set-out"
            title={!up ? OFFLINE_TITLE : isLead ? `Quick fight: ${SCENARIOS[party.scenario] ?? ''}` : 'only the lead sets out'}>Set out</Button>
        )}
        {party.state === 1 && party.encounterId && <Button primary size="lg" onClick={() => go('encounter', { encounterId: party.encounterId })}>Rejoin the fight</Button>}
      </>} />
  ) : (
    <LobbyBottom squares={parties.map((p) => ({ key: p.id.toString(), colour: colours.get(identityHex(p.lead)), on: p.state === 0, title: p.name }))}
      summary={parties.length ? <><b>{parties.length} part{parties.length === 1 ? 'y' : 'ies'}</b> in this World. Open one to fall in.</> : 'No party is forming yet. Name one to start.'}
      actions={<Button size="lg" onClick={() => go('world')} data-testid="lobby-to-world">Back to the World</Button>} />
  );

  return (
    <LobbyFrame screen="lobby" testId="lobby-screen" data-party={party ? party.id.toString() : undefined} top={top}
      main={<>{seats}<div className="fk-lobby__lower">{strip}{context}</div></>}
      side={worldId !== undefined && <ChatDock worldId={worldId} partyId={party ? partyId : undefined} variant="panel" className="fk-lobby__chat" title="World chat" here={here} />}
      bottom={<>
        {bottom}
        {worldId !== undefined && <Modal open={forging} onClose={() => setForging(false)} eyebrow={world && worldTitle(world)} title="Forge a hero" size="full" testId="forge-modal">
          <HeroForge worldId={worldId} rosterFull={rosterFull} fee={recruit.fee} capNote={recruit.why} onForged={() => setForging(false)} />
        </Modal>}
        {worldId !== undefined && <Modal open={inviting} onClose={() => setInviting(false)} eyebrow={world && worldTitle(world)} title="Invite a friend" size="narrow" testId="invite-modal">
          <FriendsPanel inviteWorldId={worldId} className="fk-lobby__friends" />
        </Modal>}
      </>} />
  );
}
